import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useVehicles } from '@/hooks/useVehicles'
import { useReservations } from '@/hooks/useReservations'
import {Car, Calendar, Users, Settings, Plus, Edit, Trash2, ChevronLeft, Sparkles, TrendingUp, Clock, Shield, Zap, CreditCard, Eye, Power, ToggleLeft, ToggleRight, Percent} from 'lucide-react'
import VehicleExcelManager from '@/components/VehicleExcelManager'
import DiscountRulesManager from '@/components/DiscountRulesManager'
import OperationalReservations from '@/components/OperationalReservations'

export default function AdminDashboard() {
  const { user, isAdmin } = useAuth()
  const navigate = useNavigate()
  const { vehicles, loading: vehiclesLoading, fetchVehicles, updateVehicle, deleteVehicle } = useVehicles()
  const { reservations, loading: reservationsLoading, fetchReservations, updateReservation, deleteReservation } = useReservations()

  const [activeTab, setActiveTab] = useState<'vehicles' | 'reservations' | 'discounts'>('vehicles')

  useEffect(() => {
    if (!isAdmin) {
      navigate('/')
      return
    }
    fetchVehicles()
    fetchReservations({ sort: { createdAt: -1 } })
  }, [isAdmin])

  const [togglingId, setTogglingId] = useState<string | null>(null)

  const handleDeleteVehicle = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) {
      try {
        await deleteVehicle(id)
        alert('Véhicule supprimé avec succès')
      } catch (error) {
        alert('Erreur lors de la suppression')
      }
    }
  }

  const getVehicleStatusInfo = (status: string) => {
    const s = status?.toLowerCase()
    if (['available', 'disponible'].includes(s)) return { level: 'active', label: 'Actif', color: 'green' }
    if (['indisponible', 'unavailable', 'maintenance', 'rented'].includes(s)) return { level: 'inactive', label: 'Indisponible', color: 'orange' }
    if (s === 'cache') return { level: 'hidden', label: 'Caché', color: 'red' }
    return { level: 'inactive', label: status, color: 'gray' }
  }

  // Cycle: disponible -> indisponible -> caché -> disponible
  const handleToggleVehicle = async (vehicle: any) => {
    const info = getVehicleStatusInfo(vehicle.status)
    let newStatus: string
    let actionLabel: string
    if (info.level === 'active') {
      newStatus = 'indisponible'
      actionLabel = 'rendre indisponible (visible mais non réservable)'
    } else if (info.level === 'inactive') {
      newStatus = 'cache'
      actionLabel = 'cacher (invisible sur le site)'
    } else {
      newStatus = 'disponible'
      actionLabel = 'rendre disponible (visible et réservable)'
    }

    if (!window.confirm(`Voulez-vous ${actionLabel} le véhicule "${vehicle.name}" ?`)) return

    setTogglingId(vehicle._id)
    try {
      await updateVehicle(vehicle._id, { status: newStatus, updatedAt: new Date().toISOString() })
      await fetchVehicles()
    } catch (error) {
      alert('Erreur lors de la mise à jour du statut')
    } finally {
      setTogglingId(null)
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black',
      confirmed: 'bg-gradient-to-r from-blue-400 to-blue-600 text-white',
      paid: 'bg-gradient-to-r from-green-400 to-green-600 text-white',
      active: 'bg-gradient-to-r from-purple-400 to-purple-600 text-white',
      completed: 'bg-gradient-to-r from-gray-400 to-gray-600 text-white',
      cancelled: 'bg-gradient-to-r from-red-400 to-red-600 text-white',
    }
    return colors[status] || 'bg-gray-500 text-white'
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      paid: 'Payée',
      active: 'En cours',
      completed: 'Terminée',
      cancelled: 'Annulée',
    }
    return labels[status] || status
  }

  const handleDeleteReservation = async (id: string) => {
    if (window.confirm('Supprimer cette reservation ?')) {
      try {
        await deleteReservation(id)
        alert('Reservation supprimee')
        fetchReservations({ sort: { createdAt: -1 } })
      } catch (error) {
        alert('Erreur lors de la suppression')
      }
    }
  }

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await updateReservation(id, { status: newStatus, updatedAt: new Date().toISOString() })
      fetchReservations({ sort: { createdAt: -1 } })
    } catch (error) {
      alert('Erreur lors de la mise a jour du statut')
    }
  }

  if (!isAdmin) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header Admin */}
      <div className="bg-black text-white py-12 border-b border-yellow-400/20 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 mb-4 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 p-4 rounded-2xl shadow-lg">
              <Shield className="w-10 h-10 text-black" />
            </div>
            <div>
              <h1 className="text-4xl font-black bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">
                Administration
              </h1>
              <p className="text-gray-400 mt-1 text-lg">Bienvenue, {user?.userName}</p>
            </div>
            <div className="ml-auto">
              <button
                onClick={() => navigate('/agenda')}
                className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-6 py-3 rounded-xl hover:shadow-2xl hover:shadow-yellow-400/50 transition-all font-bold hover:scale-105"
              >
                <Calendar className="w-5 h-5" />
                Voir l'agenda
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-xl p-6 border-2 border-yellow-400/30 hover:border-yellow-400 transition-all hover:shadow-yellow-400/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400 mb-1">Total véhicules</p>
                <p className="text-4xl font-black bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">{vehicles.length}</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 p-3 rounded-xl shadow-lg">
                <Car className="w-8 h-8 text-black" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-xl p-6 border-2 border-blue-400/30 hover:border-blue-400 transition-all hover:shadow-blue-400/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400 mb-1">Réservations</p>
                <p className="text-4xl font-black text-blue-400">{reservations.length}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-400 to-blue-600 p-3 rounded-xl shadow-lg">
                <Calendar className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-xl p-6 border-2 border-yellow-500/30 hover:border-yellow-500 transition-all hover:shadow-yellow-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400 mb-1">En attente</p>
                <p className="text-4xl font-black text-yellow-400">
                  {reservations.filter((r) => r.status === 'pending').length}
                </p>
              </div>
              <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 p-3 rounded-xl shadow-lg">
                <Clock className="w-8 h-8 text-black" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-xl p-6 border-2 border-green-400/30 hover:border-green-400 transition-all hover:shadow-green-400/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400 mb-1">Actives</p>
                <p className="text-4xl font-black text-green-400">
                  {reservations.filter((r) => r.status === 'active').length}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-400 to-green-600 p-3 rounded-xl shadow-lg">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl shadow-xl mb-8 border-2 border-gray-700 overflow-hidden">
          <div className="flex">
            <button
              onClick={() => setActiveTab('vehicles')}
              className={`flex-1 px-8 py-5 font-bold text-lg transition-all duration-300 relative ${
                activeTab === 'vehicles'
                  ? 'text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {activeTab === 'vehicles' && (
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-500" />
              )}
              <span className="relative flex items-center justify-center gap-2">
                <Car className="w-6 h-6" />
                Gestion des véhicules
              </span>
            </button>
            <button
              onClick={() => setActiveTab('reservations')}
              className={`flex-1 px-8 py-5 font-bold text-lg transition-all duration-300 relative ${
                activeTab === 'reservations'
                  ? 'text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {activeTab === 'reservations' && (
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-500" />
              )}
              <span className="relative flex items-center justify-center gap-2">
                <Calendar className="w-6 h-6" />
                Réservations
              </span>
            </button>
            <button
              onClick={() => setActiveTab('discounts')}
              className={`flex-1 px-8 py-5 font-bold text-lg transition-all duration-300 relative ${
                activeTab === 'discounts'
                  ? 'text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {activeTab === 'discounts' && (
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-500" />
              )}
              <span className="relative flex items-center justify-center gap-2">
                <Percent className="w-6 h-6" />
                Rabais
              </span>
            </button>
          </div>
        </div>

        {/* Vehicles Tab */}
        {activeTab === 'vehicles' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-black text-white flex items-center gap-3">
                <Zap className="w-8 h-8 text-yellow-400" />
                Véhicules
              </h2>
              <button
                onClick={() => navigate('/admin/vehicule/nouveau')}
                className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-6 py-3 rounded-xl hover:shadow-2xl hover:shadow-yellow-400/50 transition-all font-bold hover:scale-105"
              >
                <Plus className="w-5 h-5" />
                Ajouter un véhicule
              </button>
            </div>

            {vehiclesLoading ? (
              <div className="text-center py-20">
                <div className="inline-block w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-4" />
                <div className="text-xl text-white">Chargement...</div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles.map((vehicle) => (
                  <div
                    key={vehicle._id}
                    className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-yellow-400/20 transition-all duration-300 border-2 border-gray-700 hover:border-yellow-400"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={vehicle.imageUrl}
                        alt={vehicle.name}
                        className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${getVehicleStatusInfo(vehicle.status).level === 'hidden' ? 'grayscale opacity-40' : getVehicleStatusInfo(vehicle.status).level === 'inactive' ? 'opacity-70' : ''}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                        getVehicleStatusInfo(vehicle.status).color === 'green'
                          ? 'bg-gradient-to-r from-green-400 to-green-500 text-black'
                          : getVehicleStatusInfo(vehicle.status).color === 'orange'
                          ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-black'
                          : 'bg-gradient-to-r from-red-400 to-red-500 text-white'
                      }`}>
                        {getVehicleStatusInfo(vehicle.status).label}
                      </div>
                      <div className="absolute top-3 right-3 bg-black/60 text-gray-300 px-3 py-1 rounded-full text-xs font-bold">
                        {vehicle.status}
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className={`text-xl font-black ${getVehicleStatusInfo(vehicle.status).level === 'active' ? 'text-white' : getVehicleStatusInfo(vehicle.status).level === 'inactive' ? 'text-orange-300' : 'text-gray-500'}`}>{vehicle.name}</h3>
                        <button
                          onClick={() => handleToggleVehicle(vehicle)}
                          disabled={togglingId === vehicle._id}
                          title={
                            getVehicleStatusInfo(vehicle.status).level === 'active' ? 'Rendre indisponible' :
                            getVehicleStatusInfo(vehicle.status).level === 'inactive' ? 'Cacher le véhicule' :
                            'Rendre disponible'
                          }
                          className={`flex-shrink-0 ml-2 p-1 rounded-lg transition-all hover:scale-110 ${
                            togglingId === vehicle._id ? 'opacity-50 cursor-wait' : ''
                          }`}
                        >
                          {togglingId === vehicle._id ? (
                            <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                          ) : getVehicleStatusInfo(vehicle.status).level === 'active' ? (
                            <ToggleRight className="w-8 h-8 text-green-400 hover:text-green-300" />
                          ) : getVehicleStatusInfo(vehicle.status).level === 'inactive' ? (
                            <Power className="w-8 h-8 text-orange-400 hover:text-orange-300" />
                          ) : (
                            <ToggleLeft className="w-8 h-8 text-red-400 hover:text-red-300" />
                          )}
                        </button>
                      </div>
                      <p className="text-sm text-gray-400 mb-4">
                        {vehicle.brand} {vehicle.model} • {vehicle.category}
                      </p>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                        <span className="text-2xl font-black bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
                          CHF {vehicle.pricePerDay}/j
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/admin/vehicule/${vehicle._id}`)}
                            className="p-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all hover:scale-110 shadow-lg"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteVehicle(vehicle._id)}
                            className="p-2.5 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-all hover:scale-110 shadow-lg"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reservations Tab */}
        {activeTab === 'reservations' && (
          <OperationalReservations
            reservations={reservations as any}
            loading={reservationsLoading}
            onEdit={(id) => navigate(`/admin/reservation/${id}`)}
            onDelete={handleDeleteReservation}
            onStatusChange={handleStatusChange}
            onCreate={() => navigate('/admin/reservation/nouvelle')}
          />
        )}

        {/* Ancienne vue (désactivée, conservée pour référence) */}
        {false && activeTab === 'reservations' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-black text-white flex items-center gap-3">
                <Calendar className="w-8 h-8 text-yellow-400" />
                Réservations
              </h2>
              <button
                onClick={() => navigate('/admin/reservation/nouvelle')}
                className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-6 py-3 rounded-xl hover:shadow-2xl hover:shadow-yellow-400/50 transition-all font-bold hover:scale-105"
              >
                <Plus className="w-5 h-5" />
                Ajouter manuellement
              </button>
            </div>

            {reservationsLoading ? (
              <div className="text-center py-20">
                <div className="inline-block w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-4" />
                <div className="text-xl text-white">Chargement...</div>
              </div>
            ) : reservations.length === 0 ? (
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-16 text-center border-2 border-gray-700">
                <Calendar className="w-20 h-20 text-yellow-400 mx-auto mb-6 opacity-50" />
                <h3 className="text-2xl font-bold text-white">Aucune réservation</h3>
              </div>
            ) : (
              <div className="space-y-6">
                {reservations.map((reservation) => (
                  <div
                    key={reservation._id}
                    className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl overflow-hidden border-2 border-gray-700 hover:border-yellow-400/50 transition-all duration-300"
                  >
                    {/* Header */}
                    <div className="bg-black/50 p-6 border-b border-gray-700">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-xl font-black text-white mb-2 flex items-center gap-2">
                            <Car className="w-5 h-5 text-yellow-400" />
                            {reservation.vehicleName}
                          </h3>
                          <p className="text-sm text-gray-400 font-mono">#{reservation.reservationNumber}</p>
                        </div>
                        <span className={`px-4 py-2 rounded-xl text-sm font-bold shadow-lg ${getStatusColor(reservation.status)}`}>
                          {getStatusLabel(reservation.status)}
                        </span>
                      </div>
                    </div>

                    {/* Content Grid */}
                    <div className="p-6">
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 rounded-xl p-4 border border-blue-700/30">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="w-5 h-5 text-blue-400" />
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wide">Client</p>
                          </div>
                          <p className="font-bold text-white mb-1">{reservation.clientName}</p>
                          <p className="text-sm text-gray-400">{reservation.clientPhone}</p>
                          {reservation.clientEmail && <p className="text-sm text-gray-500">{reservation.clientEmail}</p>}
                        </div>
                        <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 rounded-xl p-4 border border-purple-700/30">
                          <div className="flex items-center gap-2 mb-3">
                            <Calendar className="w-5 h-5 text-purple-400" />
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wide">Période</p>
                          </div>
                          <p className="font-bold text-white text-sm">
                            {new Date(reservation.startDate).toLocaleDateString('fr-CH')}
                          </p>
                          <p className="text-sm text-gray-400">
                            au {new Date(reservation.endDate).toLocaleDateString('fr-CH')}
                          </p>
                        </div>
                        <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 rounded-xl p-4 border border-green-700/30">
                          <div className="flex items-center gap-2 mb-3">
                            <CreditCard className="w-5 h-5 text-green-400" />
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wide">Prix total</p>
                          </div>
                          <p className="text-3xl font-black bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
                            CHF {reservation.totalPrice}
                          </p>
                        </div>
                      </div>

                      {/* Actions + Changement rapide de statut */}
                      <div className="mt-6 pt-4 border-t border-gray-700 flex flex-wrap items-center justify-between gap-4">
                        <div className="flex flex-wrap gap-2">
                          <span className="text-sm text-gray-400 font-bold mr-2 self-center">Statut :</span>
                          {['pending', 'confirmed', 'paid', 'active', 'completed', 'cancelled'].map(s => (
                            <button
                              key={s}
                              onClick={() => handleStatusChange(reservation._id, s)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                reservation.status === s
                                  ? getStatusColor(s) + ' ring-2 ring-white shadow-lg'
                                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
                              }`}
                            >
                              {getStatusLabel(s)}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/admin/reservation/${reservation._id}`)}
                            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all hover:scale-105 text-sm font-bold shadow-lg"
                          >
                            <Edit className="w-4 h-4" />
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteReservation(reservation._id)}
                            className="flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-all hover:scale-105 text-sm font-bold shadow-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </button>
                        </div>
                      </div>

                      {/* Notes */}
                      {reservation.notes && (
                        <div className="mt-4 bg-gray-700/30 rounded-xl p-3 border border-gray-600">
                          <p className="text-xs text-gray-400 font-bold mb-1">Notes internes</p>
                          <p className="text-sm text-gray-300">{reservation.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Discounts Tab */}
        {activeTab === 'discounts' && (
          <DiscountRulesManager />
        )}
      </div>
    </div>
  )
}
